import type { PropertyFormData, StepId } from "../types";

/**
 * Determines if a step should be skipped based on form data
 */
export function shouldSkipStep(
  stepId: StepId,
  formData: Partial<PropertyFormData>
): boolean {
  switch (stepId) {
    case "sub-type":
      // Skip sub-type step if property type is "Wohnung"
      return formData.propertyType === "Wohnung";

    case "additional-size":
      // Skip additional-size step for Grundstück
      return formData.propertyType === "Grundstück";

    case "construction-year":
    case "quality":
    case "condition":
    case "usage":
      // Skip these steps for Grundstück
      return formData.propertyType === "Grundstück";

    case "property-features":
      // Skip property-features for Grundstück and Gewerbe
      return (
        formData.propertyType === "Grundstück" ||
        formData.propertyType === "Gewerbe"
      );

    case "property-search":
      // Skip property-search if reason is not Kaufabsicht
      return formData.reason !== "Kaufabsicht";

    case "timeline":
      // Skip timeline if reason is not Verkaufsabsicht or Kaufabsicht
      return (
        formData.reason !== "Verkaufsabsicht" &&
        formData.reason !== "Kaufabsicht"
      );

    default:
      return false;
  }
}

/**
 * Gets the next step based on current step and form data
 */
export function getNextStepId(
  currentStepId: StepId,
  formData: Partial<PropertyFormData>
): StepId | null {
  const stepOrder: StepId[] = [
    "property-type",
    "sub-type",
    "property-size",
    "additional-size",
    "construction-year",
    "property-features",
    "quality",
    "condition",
    "usage",
    "purpose",
    "property-search",
    "timeline",
    "address",
    "contact",
  ];

  const currentIndex = stepOrder.indexOf(currentStepId);
  if (currentIndex === -1 || currentIndex === stepOrder.length - 1) {
    return null; // Last step or invalid step
  }

  // Find the next non-skipped step
  for (let i = currentIndex + 1; i < stepOrder.length; i++) {
    const nextStep = stepOrder[i];
    if (!shouldSkipStep(nextStep, formData)) {
      return nextStep;
    }
  }

  return null;
}

/**
 * Gets the previous step based on current step and form data
 */
export function getPreviousStepId(
  currentStepId: StepId,
  formData: Partial<PropertyFormData>
): StepId | null {
  const stepOrder: StepId[] = [
    "property-type",
    "sub-type",
    "property-size",
    "additional-size",
    "construction-year",
    "property-features",
    "quality",
    "condition",
    "usage",
    "purpose",
    "property-search",
    "timeline",
    "address",
    "contact",
  ];

  const currentIndex = stepOrder.indexOf(currentStepId);
  if (currentIndex === -1 || currentIndex === 0) {
    return null; // First step or invalid step
  }

  // Find the previous non-skipped step
  for (let i = currentIndex - 1; i >= 0; i--) {
    const prevStep = stepOrder[i];
    if (!shouldSkipStep(prevStep, formData)) {
      return prevStep;
    }
  }

  return null;
}

/**
 * Gets the step number (excluding skipped steps) for display
 */
export function getStepNumber(
  stepId: StepId,
  formData: Partial<PropertyFormData>
): number {
  const stepOrder: StepId[] = [
    "property-type",
    "sub-type",
    "property-size",
    "additional-size",
    "construction-year",
    "property-features",
    "quality",
    "condition",
    "usage",
    "purpose",
    "property-search",
    "timeline",
    "address",
    "contact",
  ];

  let stepNumber = 0;
  for (const step of stepOrder) {
    if (!shouldSkipStep(step, formData)) {
      stepNumber++;
    }
    if (step === stepId) {
      break;
    }
  }

  return stepNumber;
}

/**
 * Gets total number of steps (excluding skipped ones)
 */
export function getTotalSteps(formData: Partial<PropertyFormData>): number {
  const stepOrder: StepId[] = [
    "property-type",
    "sub-type",
    "property-size",
    "additional-size",
    "construction-year",
    "property-features",
    "quality",
    "condition",
    "usage",
    "purpose",
    "property-search",
    "timeline",
    "address",
    "contact",
  ];

  return stepOrder.filter((step) => !shouldSkipStep(step, formData)).length;
}
